// Plain-words line under each call in Recent calls. Pure (no browser APIs),
// so it can be unit-tested.

import type { CallEntry } from '../api';
import { formatDuration, formatPhone } from './number';
import { shortLabel, type NumberNames } from './number-names';

/** "A", "A and B", "A, B and C", "A, B and 2 others". */
export function listPeople(numbers: readonly string[]): string {
  const n = numbers.map(formatPhone);
  if (n.length <= 2) return n.join(' and ');
  if (n.length === 3) return `${n[0]}, ${n[1]} and ${n[2]}`;
  return `${n[0]}, ${n[1]} and ${n.length - 2} others`;
}

/** Text for the history row, e.g. "Incoming, 2:05", "Declined", "Missed: you were offline". */
/**
 * Which of the user's own numbers a call used, for users with several numbers
 * (Numbers PR 1): " · from +1 (…)" on outgoing calls, " · to +1 (…)" on incoming,
 * missed and voicemail rows. '' when the user has one number or it isn't known.
 */
export function ownNumberText(
  severalNumbers: boolean,
  direction: 'inbound' | 'outbound',
  own: string | null | undefined,
  names?: NumberNames,
): string {
  if (!severalNumbers || !own) return '';
  // Numbers PR 2: a named number shows as "Sales (8570)"
  return ` · ${direction === 'outbound' ? 'from' : 'to'} ${shortLabel(own, names)}`;
}

export function describeCall(
  c: Pick<CallEntry, 'status' | 'direction' | 'durationSec' | 'reason' | 'forwardedTo' | 'forwardAnswered'> &
    Partial<Pick<CallEntry, 'transferredTo' | 'transferKind' | 'transferAnswered' | 'conferenceWith'>>,
): string {
  const dir = c.direction === 'outbound' ? 'Outgoing' : 'Incoming';
  if (c.conferenceWith?.length && c.status === 'answered') {
    const line = `${dir}, ${formatDuration(c.durationSec)} · conference with ${listPeople(c.conferenceWith)}`;
    const moved = c.transferredTo && (c.transferKind === 'blind' || c.transferKind === 'warm') && c.transferAnswered !== false;
    return moved ? `${line} · transferred to ${formatPhone(c.transferredTo!)}` : line;
  }
  if (c.transferredTo && c.status === 'answered') {
    const to = formatPhone(c.transferredTo);
    if (c.transferKind === 'conference') return `${dir}, ${formatDuration(c.durationSec)} · conference with ${to}`;
    if (c.transferAnswered === false) return `${dir}, ${formatDuration(c.durationSec)} · transfer to ${to} not answered, came back`;
    return `${dir}, ${formatDuration(c.durationSec)} · transferred to ${to}`;
  }
  if (c.direction === 'inbound' && (c.forwardedTo || c.reason === 'forwarded')) {
    const to = c.forwardedTo ? ` to ${formatPhone(c.forwardedTo)}` : '';
    if (c.status === 'answered' && c.forwardAnswered) return `Forwarded${to}, ${formatDuration(c.durationSec)}`;
    if (c.status === 'answered') return `${dir}, ${formatDuration(c.durationSec)}`; // answered in the app after all
    return `Missed: forwarded${to}, not answered`;
  }
  if (c.status === 'answered') return `${dir}, ${formatDuration(c.durationSec)}`;
  if (c.status === 'missed') {
    switch (c.reason) {
      case 'declined':
        return 'Declined';
      case 'no_answer':
        return 'Missed: not answered';
      case 'caller_hung_up':
        return 'Missed: caller hung up';
      case 'offline':
        return 'Missed: you were offline';
      case 'dnd':
        return 'Missed: Do Not Disturb was on';
      case 'paused':
        return 'Missed: calls are paused (payment overdue)';
      case 'no_balance':
        return 'Missed: no plan minutes or credit left';
      case 'not_put_through':
        // MagnusBilling refused it before ringing. It can't tell us why (no
        // balance, credit limit, plan or minutes limit, unpaid number), so the
        // text names the account. A fuller explanation comes with plan pages.
        return 'Missed: your account couldn’t take this call';
      case 'unreachable':
        return 'Missed: couldn’t reach your phone';
      case 'failed':
        return 'Missed: call failed';
      default:
        return 'Missed';
    }
  }
  switch (c.reason) {
    case 'busy':
      return 'Outgoing, busy';
    case 'no_answer':
      return 'Outgoing, no answer';
    case 'cancelled':
      return 'Outgoing, cancelled';
    case 'blocked':
      return 'Outgoing, not called: this number can’t be called';
    case 'paused':
      return 'Outgoing, not called: calls are paused (payment overdue)';
    default:
      return 'Outgoing, not connected';
  }
}

/** Repeats closer than this are one row. */
export const REPEAT_WINDOW_MS = 2 * 60_000;

export interface CallGroup<T> {
  /** newest call of the group (what the row shows) */
  call: T;
  /** how many calls the row stands for */
  count: number;
}

/**
 * Group back-to-back missed calls that are repeats of one attempt: same number,
 * same reason, each within REPEAT_WINDOW_MS of the next (carriers and callers
 * often retry at once). Input and output newest first; nothing is dropped,
 * every call is in exactly one group.
 */
export function groupRepeats<T extends Pick<CallEntry, 'status' | 'direction' | 'number' | 'reason' | 'startedAt' | 'voicemail'>>(
  calls: readonly T[],
  windowMs = REPEAT_WINDOW_MS,
): CallGroup<T>[] {
  const out: CallGroup<T>[] = [];
  let lastAt = 0; // time of the oldest call in the current group
  for (const c of calls) {
    const at = Date.parse(c.startedAt);
    const g = out[out.length - 1];
    if (
      g &&
      !c.voicemail &&
      !g.call.voicemail &&
      c.status === 'missed' &&
      g.call.status === 'missed' &&
      c.direction === 'inbound' &&
      g.call.direction === 'inbound' &&
      c.number !== '' &&
      c.number === g.call.number &&
      (c.reason ?? null) === (g.call.reason ?? null) &&
      Math.abs(lastAt - at) <= windowMs
    ) {
      g.count++;
      lastAt = at;
      continue;
    }
    out.push({ call: c, count: 1 });
    lastAt = at;
  }
  return out;
}
